import {differenceInCalendarDays, isBefore} from 'date-fns';
import {useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import {formatDate, formatDateInput, useFilterDates} from '../../utils';

export const DateRangeFilter = ({
  onApply,
  maxRangeDays = 31,
  currentRangeDays,
}) => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [popupPos, setPopupPos] = useState({top: 0, left: 0});
  const buttonRef = useRef(null);

  const {
    date: tempDateRange,
    setDate: setTempDateRange,
    resetDate,
  } = useFilterDates(currentRangeDays || maxRangeDays);
  const [dateError, setDateError] = useState('');

  const handleFilterClick = () => {
    if (!filterOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupPos({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX,
      });
    }
    setFilterOpen((prev) => !prev);
  };

  const handleDateChange = (e) => {
    const {name, value} = e.target;
    // Parse date string as local date to avoid timezone issues
    const [year, month, day] = value.split('-').map(Number);
    const newDate = new Date(year, month - 1, day);

    setTempDateRange((prev) => {
      if (name === 'startDate') {
        const daysDiff = differenceInCalendarDays(prev.endDate, newDate);

        // If end date is today and start date changes, adjust end date
        if (formatDateInput(prev.endDate) === formatDateInput(new Date())) {
          const adjustedEnd = new Date(newDate);
          adjustedEnd.setDate(adjustedEnd.getDate() + (maxRangeDays - 1));
          return {startDate: newDate, endDate: adjustedEnd};
        }

        // If range exceeds max, adjust end date
        if (daysDiff > maxRangeDays - 1) {
          const adjustedEnd = new Date(newDate);
          adjustedEnd.setDate(adjustedEnd.getDate() + (maxRangeDays - 1));
          return {startDate: newDate, endDate: adjustedEnd};
        }

        // If end date is before start date, adjust end date
        if (isBefore(prev.endDate, newDate)) {
          const adjustedEnd = new Date(newDate);
          adjustedEnd.setDate(adjustedEnd.getDate() + (maxRangeDays - 1));
          return {startDate: newDate, endDate: adjustedEnd};
        }
      }

      return {...prev, [name]: newDate};
    });
  };

  const handleApply = () => {
    if (isBefore(tempDateRange.endDate, tempDateRange.startDate)) {
      setDateError('End date cannot be before start date');
      return;
    }

    const diffDays = differenceInCalendarDays(
      tempDateRange.endDate,
      tempDateRange.startDate,
    );

    if (diffDays > maxRangeDays - 1) {
      setDateError(`Maximum range is ${maxRangeDays} days`);
      return;
    }

    setDateError('');
    setFilterOpen(false);

    onApply({
      startDate: tempDateRange.startDate,
      endDate: tempDateRange.endDate,
    });
  };

  const handleReset = () => {
    const defaultRange = resetDate();
    setTempDateRange(defaultRange);
    setDateError('');
    onApply(defaultRange);
  };

  return (
    <div className='date-range-display'>
      <div className='filter-container' style={{position: 'relative'}}>
        <button
          className='filter-button'
          ref={buttonRef}
          onClick={handleFilterClick}
        >
          Filter
        </button>

        {filterOpen &&
          createPortal(
            <div
              className='filter-popup'
              style={{
                position: 'absolute',
                top: popupPos.top,
                left: popupPos.left,
              }}
            >
              <h3>Filter by Date Range</h3>

              {dateError && <div className='date-error'>{dateError}</div>}

              <div className='filter-inputs'>
                <div className='filter-input-group'>
                  <label>Start Date</label>
                  <input
                    type='date'
                    name='startDate'
                    value={formatDateInput(tempDateRange.startDate)}
                    onChange={handleDateChange}
                  />
                </div>

                <div className='filter-input-group'>
                  <label>End Date</label>
                  <input
                    type='date'
                    name='endDate'
                    value={formatDateInput(tempDateRange.endDate)}
                    onChange={handleDateChange}
                  />
                </div>
              </div>

              <div className='filter-actions'>
                <button className='filter-apply-button' onClick={handleApply}>
                  Apply Filter
                </button>
                <button className='filter-reset-button' onClick={handleReset}>
                  Reset
                </button>
                <button
                  className='filter-cancel-button'
                  onClick={() => setFilterOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>,
            document.body,
          )}

        <span className='impairment-date-range-text'>{`${formatDate(
          tempDateRange.startDate,
        )} - ${formatDate(tempDateRange.endDate)}`}</span>
      </div>
    </div>
  );
};
